import { debounce } from 'lodash-es';
import { orderList } from '~/api';
import {
  USER_LEVEL_KEY,
  type IAddPrice,
  type IEquity,
  type IFees,
  type IRightsCompare,
} from '~/api/interface';
import type { IOrder, ISubscription } from '~/api/interface/order.ts';
import type { RIGHTS_FEATURE } from '~/api/interface/rights';
import {
  BUY_TYPE,
  ORDER_BEHAVIOR,
  ORDER_TYPE,
  PAY_STATUS,
  PAY_TYPE,
  SUBSCRIBE_STATUS,
} from '~/api/interface/rights';
import {
  getOrderClientRemainedBuyCreditCoinsCount,
  getSubscriptionStatus,
  postCreateOrder,
  postOrderClientBuyCreditPackage,
  postOrderClientBuyStorage,
  postOrderClientSubscriptionCreate,
  reqChangePayType,
  reqCreateOrder,
  reqOrder,
  reqRightsDisplay,
  type ICreditCoinsPackage,
  type IStripePaymentBasic,
} from '~/api/module/rights.ts';
import { RIGHTS_TIME } from '~/constants';
import { PaymentRedirectUrlKey } from '~/constants/token';
import { useSingleOrderModal } from '.';
import { useTransferIntercept } from './useTransferIntercept';

function personalOrTeam(rights_type: USER_LEVEL_KEY, personal: TRACK_EVENT, team: TRACK_EVENT) {
  return [USER_LEVEL_KEY.PERSONAL, USER_LEVEL_KEY.TASTE, USER_LEVEL_KEY.PERSONAL_LITE].includes(
    rights_type
  )
    ? personal
    : team;
}

// todo 埋点
export function addTrack(record?: IOrder | ISubscription) {
  if (!record) {
    return;
  }
  let $trackEvent = personalOrTeam(
    record.rights_type,
    TRACK_EVENT.PERSONAL_PACKAGE_PAYED,
    TRACK_EVENT.TEAM_PACKAGE_PAYED
  ); // 个人，团队 套餐

  const params = {
    PayTime: record.create_time,
    PayMethod: record.pay_type,
    expired_time: record.expired_time,
    PayPackage: record.time_identity === RIGHTS_TIME().month ? 1 : 2,
    pay_amount: record.pay_fee,
    total_credit_coin: record.total_credit_coin,
    buy_type: record.buy_type,
  };
  if (record.buy_type === BUY_TYPE.SUBSCRIBE) {
    // 这里要注意，是否有结果页面，如果有结果页面，要考虑结果页面相同埋点
    $trackEvent = personalOrTeam(
      record.rights_type,
      TRACK_EVENT.PERSONAL_PACKAGE_SUBSCRIBE,
      TRACK_EVENT.TEAM_PACKAGE_SUBSCRIBE
    );
  }

  if (record.order_type === ORDER_TYPE.RENDER) {
    $trackEvent = TRACK_EVENT.DEDICATED_LINE_PAYED;
  } else if (record.order_type === ORDER_TYPE.SUBLICENSE) {
    $trackEvent = TRACK_EVENT.SUBLICENSE_PAYED;
  } else if (record.order_type === ORDER_TYPE.CREDITS) {
    $trackEvent = TRACK_EVENT.CREDIT_PAYED;
    Object.assign(params, { PayTokenNum: record.buy_num });
  } else if (record.order_type === ORDER_TYPE.STORAGE) {
    $trackEvent = TRACK_EVENT.STORAGE_PAYED;
  } else if (record.order_type === ORDER_TYPE.PACKAGE) {
    $trackEvent = personalOrTeam(
      record.rights_type,
      TRACK_EVENT.PERSONAL_PACKAGE_PAYED,
      TRACK_EVENT.TEAM_PACKAGE_PAYED
    );
  }
  trackEvent($trackEvent, params);
}

export interface ICurrentFee {
  id: number;
  time_identity: string;
  price: number;
  days: number;
}

/**
 * 用于套餐购买时，获取套餐信息、费用、价格等
 * @param rightsType 需要购买的权益ID
 */
export function useRights(rightsType?: USER_LEVEL_KEY, rightFilter?: (fees: IFees[]) => IFees) {
  const { $t } = useNuxtApp();

  if (!rightsType) {
    throw new Error('RightsTypeError');
  }

  // const userInfo = useUserInfo()
  const rights = ref<IEquity & { remained_credit_coins?: number }>();
  const rightsCompare = ref<IRightsCompare>();
  const addPrice = ref<IAddPrice>();
  const currentFees = reactive({
    id: 0,
    time_identity: '',
    price: 0,
    days: 0,
    buy_type: BUY_TYPE.PAYMENT,
  });

  function setFees(fees: IFees) {
    currentFees.id = fees.id;
    currentFees.time_identity = fees.time_identity;
    currentFees.price = fees.price;
    currentFees.days = fees.days;
    currentFees.buy_type = fees.buy_type;
    addPrice.value = fees.add_price; // 单价
  }

  async function getRights() {
    {
      const { code, data } = await reqRightsDisplay();
      if (!code && data) {
        const $rights = data.rights.find(d => d.rights_type === rightsType);
        const $rightsDesc = data.rights_compare.find(v => v.idx === 0);
        if ($rights) {
          if (rightFilter) {
            setFees(rightFilter($rights.rights_by_time));
          } else {
            setFees($rights.rights_by_time[0]);
          }
        }
        rights.value = $rights;
        rightsCompare.value = $rightsDesc;
      } else {
        message.error($t('huo-qu-tao-can-xin-xi-shi-bai'));
      }
    }
    // 补全剩余的言币购买信息
    {
      const { code, data } = await getOrderClientRemainedBuyCreditCoinsCount();
      if (!code && data && rights.value) {
        rights.value.remained_credit_coins = data.remained_credit_coins;
      }
    }
  }

  return {
    rights,
    getRights,
    rightsCompare,
    currentFees,
    setFees,
    addPrice,
  };
}

interface ICoinBuyOrderBody {
  packageInfo?: ICreditCoinsPackage;
}

interface IStorageBuyOrderBody {
  parent_order_id: number;
  num: number;
  currentPackage: any;
}

interface IBasicOverseaPaymentInfo {
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface ICommonBuyOrderBody extends IBasicOverseaPaymentInfo {
  behavior: ORDER_BEHAVIOR;
  rights_type: USER_LEVEL_KEY;
  time_identity: string;
  product_url: string;
  rightsName: string;
  rights_id: number;
  featureType: RIGHTS_FEATURE;
  unitPrice: number;
  days?: number;
  buy_type: BUY_TYPE;
  order_type: ORDER_TYPE;
  pay_type: PAY_TYPE;
}

export default function useOrder(
  orderBody: Ref<Partial<ICommonBuyOrderBody & ICoinBuyOrderBody & IStorageBuyOrderBody>>,
  paySuccessUpdateUser = true,
  headers?: Record<string, string>
) {
  let timer: number = -1;

  const abort = ref<AbortController>();
  const extraConfig: Record<string, any> = {};
  if (headers) {
    extraConfig.headers = headers;
  }

  const creating = ref(false);
  const rawOrder = ref<IOrder>();
  const agreementChecked = ref(!!isGlobalVersion());
  const payType = ref(isGlobalVersion() ? PAY_TYPE.AIRWALLEX : PAY_TYPE.ALIPAY);
  const POLLING_INTERVAL = 3000;

  const currentRequest = ref();

  const qrcode = reactive({
    weixin: '',
    alipay: '',
  });

  const order = reactive<{
    id?: number;
    status?: PAY_STATUS;
  }>({});

  const paySucc = computed(() => order.status === PAY_STATUS.PAID);

  function stopPolling() {
    clearInterval(timer);
    timer = -1;
  }

  function startPolling() {
    if (timer === -1) {
      timer = window.setInterval(getOrder, POLLING_INTERVAL);
    }
  }

  function onRePay() {
    order.status = undefined;
    createOrder();
  }

  async function getOrder() {
    if (!order.id) {
      return;
    }

    if (orderBody.value.buy_type === BUY_TYPE.SUBSCRIBE) {
      const res = await getSubscriptionStatus(order.id, extraConfig);
      if (res.data) {
        if (res.data.order.qr_code_url !== qrcode.alipay) {
          if (payType.value === PAY_TYPE.ALIPAY) {
            qrcode.alipay = res.data.order.qr_code_url;
          } else {
            qrcode.weixin = res.data.order.qr_code_url;
          }
        }
        if (![PAY_STATUS.PAYING, PAY_STATUS.UNPAID].includes(res.data.subscription.status)) {
          if (res.data.subscription.status === SUBSCRIBE_STATUS.SUBSCRIBED) {
            if (res.data.order.id && res.data.subscription.show_qr_code) {
              stopPolling();
              useSingleOrderModal(
                res.data.order,
                res.data.subscription.pay_error === 'ACQ.CYCLE_PAY_TIME_NOT_SUPPORT'
                  ? '根据支付宝的限制，在夜间22:00-次日7:00无法进行连续包月扣费，请扫码完成支付'
                  : '根据支付宝的限制，连续包月套餐30天内无法进行二次扣费，为保证您下月套餐按时生效，请扫码支付本次升级费用',
                () => addTrack(rawOrder.value)
              );
            } else if (![PAY_STATUS.PAYING, PAY_STATUS.UNPAID].includes(res.data.order.status)) {
              order.status = res.data.order.status;

              stopPolling();
              if (res.data.order.status === PAY_STATUS.PAID && rawOrder.value) {
                addTrack(rawOrder.value);
              }
            }
          }
        }
      }
    } else {
      const res = await reqOrder(order.id, extraConfig);
      if (res.data) {
        if (res.data.qr_code_url !== qrcode.alipay) {
          if (payType.value === PAY_TYPE.ALIPAY) {
            qrcode.alipay = res.data.qr_code_url;
          } else {
            qrcode.weixin = res.data.qr_code_url;
          }
        }
        if (![PAY_STATUS.PAYING, PAY_STATUS.UNPAID].includes(res.data.status)) {
          order.status = res.data.status;
          stopPolling();
          if (res.data.status === PAY_STATUS.PAID) {
            addTrack(res.data);
          }
        }
      }
    }
  }

  async function createOrder(isTransfer?: boolean) {
    stopPolling();

    // 退出正在进行 pending 的请求
    if (abort.value) {
      abort.value.abort();
    }

    abort.value = new AbortController();

    const userInfo = useUserInfo();
    const { $t } = useNuxtApp();
    creating.value = true;

    // 必须勾选同意协议
    if (!agreementChecked.value && !isTransfer) {
      return;
    }

    if (!isGlobalVersion()) {
      const orderType = orderBody.value.order_type!;

      const pay_type = isTransfer ? PAY_TYPE.TRANSFER : orderBody.value.pay_type || payType.value;

      if (orderType === ORDER_TYPE.CREDITS) {
        // 加购言币
        const credit_coins_package_id = orderBody.value.packageInfo?.id;

        if (!pay_type || !credit_coins_package_id) {
          return;
        }

        currentRequest.value = postOrderClientBuyCreditPackage(
          {
            pay_type,
            credit_coins_package_id,
          },
          {
            ...extraConfig,
            signal: abort.value.signal,
          }
        );
      } else if (orderType === ORDER_TYPE.STORAGE) {
        currentRequest.value = postOrderClientBuyStorage(
          {
            pay_type,
            num: orderBody.value.num!,
            first_name: orderBody.value.first_name!,
            last_name: orderBody.value.last_name,
            email: orderBody.value.email!,
            is_GB: orderBody.value.currentPackage?.type === 'GB'!,
          },
          {
            ...extraConfig,
            signal: abort.value.signal,
          }
        );
      } else if (orderBody.value.buy_type === BUY_TYPE.SUBSCRIBE) {
        currentRequest.value = postOrderClientSubscriptionCreate(
          {
            rights_id: +orderBody.value.rights_id!,
            behavior: orderBody.value.behavior! || ORDER_BEHAVIOR.BUY,
          },
          {
            ...extraConfig,
            signal: abort.value.signal,
          }
        );
      } else {
        currentRequest.value = reqCreateOrder(
          {
            pay_type,
            rights_type: orderBody.value.rights_type!,
            behavior: orderBody.value.behavior!,
            time_identity: orderBody.value.time_identity!,
          },
          {
            ...extraConfig,
            signal: abort.value.signal,
          }
        );
      }

      const res = await currentRequest.value;

      if (!res.data) {
        return;
      }

      order.id = res.data.id;

      const qrcode =
        (res.data as ISubscription).subscription_url || (res.data as IOrder).qr_code_url;

      if (isTransfer) {
        const url = new URL(location.href);
        const doAlert = url.searchParams.get('alert');
        if (doAlert) {
          alert(`${qrcode}xxxx`);
        } else {
          location.href = qrcode;
        }
        return;
      }

      setQrcode(qrcode);
      startPolling();
    } else {
      sessionSetItem(PaymentRedirectUrlKey, location.href);

      // 海外的订购逻辑
      const overseaBasic: IStripePaymentBasic & {
        pay_type: PAY_TYPE;
      } = {
        pay_type: PAY_TYPE.STRIPE,
        success_url: `${location.origin}/paymentResult`,
        cancel_url: `${location.origin}/subscribe`,
        first_name: $t('yong-hu'),
        last_name: userInfo.value?.account_name,
        email: userInfo.value?.email_address || '',
        ...orderBody.value,
      };

      const orderType = orderBody.value.order_type!;

      // 言币加购逻辑
      if (orderType === ORDER_TYPE.STORAGE) {
        currentRequest.value = postOrderClientBuyStorage(
          {
            ...overseaBasic,
            num: orderBody.value.num!,
            is_GB: orderBody.value.currentPackage?.type === 'GB'!,
          },
          {
            ...extraConfig,
            signal: abort.value.signal,
          }
        );
      } else if (orderType === ORDER_TYPE.CREDITS) {
        const filtered = {
          ...overseaBasic,
          credit_coins_package_id: orderBody.value.packageInfo!.id,
        };
        currentRequest.value = postOrderClientBuyCreditPackage(filtered, {
          ...extraConfig,
          signal: abort.value.signal,
        });
      } else {
        if (!orderBody.value.time_identity) {
          return;
        }
        currentRequest.value = postCreateOrder(
          {
            rights_id: +orderBody.value.rights_id!,
            product_url: orderBody.value.product_url!,
            ...overseaBasic,
          },
          {
            ...extraConfig,
            signal: abort.value.signal,
          }
        );
      }

      const res = await currentRequest.value;

      if (!res.data) {
        return;
      }

      rawOrder.value = res.data;
    }
    creating.value = false;
  }

  async function changePayType() {
    stopPolling();

    // 退出正在进行 pending 的请求
    if (abort.value) {
      abort.value.abort();
    }

    abort.value = new AbortController();
    if (!order.id) {
      return;
    }

    currentRequest.value = reqChangePayType(
      {
        id: order.id,
        pay_type: payType.value,
      },
      {
        ...extraConfig,
        signal: abort.value.signal,
      }
    );

    const res = await currentRequest.value;

    if (!res.code && res.data) {
      setQrcode(res.data.qr_code_url);
      startPolling();
      creating.value = false;
    }
  }

  function setQrcode(url: string) {
    if (payType.value === PAY_TYPE.WEIXIN) {
      qrcode.weixin = url;
    }
    if (payType.value === PAY_TYPE.ALIPAY) {
      qrcode.alipay = url;
    }
  }

  function gen(_payType: PAY_TYPE) {
    stopPolling();

    if (!agreementChecked.value) {
      return;
    }

    if (_payType === PAY_TYPE.WEIXIN) {
      qrcode.weixin = '';
    }
    if (_payType === PAY_TYPE.ALIPAY) {
      qrcode.alipay = '';
    }

    changePayType();
  }

  const debounceGen = debounce(gen, 500);

  watch(
    () => payType.value,
    _payType => {
      stopPolling();
      if (orderBody.value.buy_type !== BUY_TYPE.SUBSCRIBE) {
        creating.value = true;
        debounceGen(_payType);
      }
    }
  );

  watch(
    () => orderBody.value,
    () => {
      // 清空二维码后重新创建订单
      payType.value = isGlobalVersion() ? PAY_TYPE.AIRWALLEX : PAY_TYPE.ALIPAY;
      qrcode.weixin = '';
      qrcode.alipay = '';
      createOrder();
    },
    { deep: true }
  );

  watch(
    () => agreementChecked.value,
    () => {
      if (agreementChecked.value) {
        if (qrcode.weixin === '' && qrcode.alipay === '') {
          createOrder();
        } else {
          startPolling();
        }
      } else {
        stopPolling();
      }
    },
    {
      deep: true,
      immediate: true,
    }
  );

  watch(
    () => paySucc.value,
    success => {
      if (success && paySuccessUpdateUser) {
        // 支付成功 3s 后关闭刷新页面
        stopPolling();
      }
    }
  );

  onBeforeUnmount(() => {
    if (abort.value) {
      abort.value.abort();
    }
    stopPolling();
  });

  const checkExistingTransferOrder = async () => {
    const res = await orderList({
      pay_type: PAY_TYPE.TRANSFER,
      status: PAY_STATUS.UNPAID,
      order_type: orderBody.value.order_type,
    });
    const orderDetail = res.data?.results?.[0] as unknown as IOrder;
    if (orderDetail) {
      useTransferIntercept({
        order: orderDetail,
      });
    }
  };

  onMounted(() => {
    // 查询是否存在生效的对公订单
    checkExistingTransferOrder();
  });

  return {
    agreementChecked,
    payType,
    qrcode,
    order,
    rawOrder,
    paySucc,
    onRePay,
    createOrder,
    creating,
  };
}
